import isEqual from "lodash.isequal"
import { createActions, createReducer } from "reduxsauce"
import Immutable from "seamless-immutable"

import { preferencesSuccess } from "../User/redux/user.actions"

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
  getAdminInvoicesFilterOptionsRequest: null,
  getAdminInvoicesFilterOptionsSuccess: ["payload"], // payload: { data }
  getAdminInvoicesFilterOptionsFailure: ["payload"], // payload: { data }

  getAdminInvoicesRequest: ["payload"], // payload: { params }
  getAdminInvoicesSuccess: ["payload"], // payload: { data }

  changeInvoiceAmountRequest: ["payload"], // payload: { id }
  changeInvoiceAmountSuccess: ["payload"], // payload: { newInvoice }

  updateAdminInvoiceRequest: ["payload"], // payload: { data }
  updateAdminInvoiceSuccess: ["payload"], // payload: { updateInvoice }

  confirmInvoiceRequest: ["payload"], // payload: { data }
  confirmInvoiceSuccess: ["payload"], // payload: { updateInvoice }

  adminInvoicesFailure: ["payload"], // payload: { errors }

  clearAdminInvoicesFilters: null,
  adminInvoicesSetProp: ["key", "value"],

  getAdminInvoicesExportRequest: ["payload"], // payload: params {}
  getAdminInvoicesExportSuccess: ["payload"], // payload: { task_id, message }
  getAdminInvoicesExportFailure: ["payload"], // payload: { errors }

  logout: null
})

export const AdminInvoicesTypes = Types
export default Creators

/* ------------- Initial State ------------- */

const columnsAdmin = [
  { field_id: "email", label: "User email", active: true },
  { field_id: "full_name", label: "User full name", active: true },
  { field_id: "loyal_billing", label: "Loyal billing", active: true },
  { field_id: "payment_system", label: "Payment system", active: true },
  { field_id: "type", label: "Type", active: true },
  { field_id: "invoice_amount", label: "Invoice amount", active: true },
  { field_id: "invoice_date", label: "Invoice date", active: true },
  { field_id: "invoice_id", label: "Invoice ID", active: true },
  { field_id: "business_account", label: "Business account", active: false },
  { field_id: "alerts_period", label: "Alerts period", active: false }
  // {field_id: 'is_reissued', label: 'Reissued', active: false},
  // {field_id: 'charge_by', label: 'Charge by', active: false},
]

export const INITIAL_STATE = Immutable({
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  defaultColumnsAdmin: [{ field_id: "status", label: "Status" }, ...columnsAdmin],
  columnsAdmin: [...columnsAdmin],

  filtersLastUpd: null,
  filters: {
    status: "all",

    full_name: [],
    email: [],
    business_account: [],

    invoice_amount: { min: null, max: null, type: null },

    alerts_period: null,
    invoice_date: null,
    admin_invoice_id: [],
    is_reissued: "all",
    exclude_fields: []
  },

  fetchingFilterOptions: false,
  filterOptions: {
    status: [],
    country: [],
    bank_name: [],
    connected_payment_processors: []
  },

  visibleFiltersAdmin: { status: true, email: true, loyal_billing: true, payment_system: true },

  exportFetching: false,
  exportResult: null
})

/* ------------- Selectors ------------- */
export const selectAreFiltersDefault = (state) =>
  isEqual(state.adminInvoices.filters, INITIAL_STATE.filters)

/* ------------- Functions for reducer cases ------------- */
const getAdminInvoicesFilterOptionsRequest = (state) => state.merge({ fetchingFilterOptions: true })
const getAdminInvoicesFilterOptionsSuccess = (state, { payload: filterOptions }) =>
  state.merge({ fetchingFilterOptions: false, filterOptions })
const getAdminInvoicesFilterOptionsFailure = (state) =>
  state.merge({ fetchingFilterOptions: false })

const getAdminInvoicesRequest = (state) => state.merge({ fetching: true })
const getAdminInvoicesSuccess = (state, { payload: result }) => {
  if (result.results) {
    return state.merge({ fetching: false, result })
  } else {
    return state.merge({
      fetching: false,
      result: {
        count: result?.length || 0,
        results: result
      }
    })
  }
}

const changeInvoiceAmountRequest = (state) => state.merge({ fetching: true })
const changeInvoiceAmountSuccess = (state, { payload: newInvoice }) => {
  return state.merge({
    fetching: false,
    result: {
      ...state.result,
      results: state.result.results.map((el) => (el.id === newInvoice.id ? newInvoice : el))
    }
  })
}

const updateAdminInvoiceRequest = (state) => state.merge({ fetching: true })
const updateAdminInvoiceSuccess = (state, { payload: newInvoice }) => {
  return state.merge({
    fetching: false,
    result: {
      ...state.result,
      results: state.result.results.map((el) => (el.id === newInvoice.id ? newInvoice : el))
    }
  })
}

const confirmInvoiceRequest = (state) => state.merge({ fetching: true })
const confirmInvoiceSuccess = (state, { payload: newInvoice }) => {
  return state.merge({
    fetching: false,
    result: {
      ...state.result,
      results: state.result.results.map((el) => (el.id === newInvoice.id ? newInvoice : el))
    }
  })
}

const adminInvoicesFailure = (state) => state.merge({ fetching: false })

export const adminInvoicesSetProp = (state, { key, value }) => {
  const obj = {}
  if (["filters", "visibleFilters", "visibleFiltersAdmin"]?.includes(key)) {
    if (key === "filters") obj.filtersLastUpd = new Date().getTime()
    obj[key] = { ...state?.[key], ...value }
  } else {
    obj[key] = value
  }
  return state.merge(obj)
}

export const clearAdminInvoicesFilters = (state) => {
  return state.merge({ filters: INITIAL_STATE.filters })
}

const getAdminInvoicesExportRequest = (state) => state.merge({ exportFetching: true })
const getAdminInvoicesExportSuccess = (state, { payload: exportResult }) =>
  state.merge({
    exportFetching: false,
    exportResult
  })
const getAdminInvoicesExportFailure = (state) => state.merge({ exportFetching: false })

const logout = (state) => {
  return state.merge({
    ...INITIAL_STATE,
    filters: state.filters,
    columnsAdmin: state.columnsAdmin,
    visibleFiltersAdmin: state.visibleFiltersAdmin
  })
}

// Bump when the shape of the saved `{ version, filters, columnsAdmin, visibleFiltersAdmin }`
// payload changes.
export const ADMIN_INVOICES_PREFERENCES_VERSION = 1

const applyPreferences = (state, { payload }) => {
  const preference = payload?.["admin-invoices"]?.data
  if (!preference || preference.version !== ADMIN_INVOICES_PREFERENCES_VERSION) return state
  if (!preference.filters || !preference.columnsAdmin || !preference.visibleFiltersAdmin)
    return state

  return state.merge({
    filters: preference.filters,
    columnsAdmin: preference.columnsAdmin,
    visibleFiltersAdmin: preference.visibleFiltersAdmin
  })
}

/* ------------- Hookup Reducers To Types ------------- */
export const reducer = createReducer(INITIAL_STATE, {
  [Types.GET_ADMIN_INVOICES_FILTER_OPTIONS_REQUEST]: getAdminInvoicesFilterOptionsRequest,
  [Types.GET_ADMIN_INVOICES_FILTER_OPTIONS_SUCCESS]: getAdminInvoicesFilterOptionsSuccess,
  [Types.GET_ADMIN_INVOICES_FILTER_OPTIONS_FAILURE]: getAdminInvoicesFilterOptionsFailure,

  [Types.GET_ADMIN_INVOICES_REQUEST]: getAdminInvoicesRequest,
  [Types.GET_ADMIN_INVOICES_SUCCESS]: getAdminInvoicesSuccess,

  [Types.CHANGE_INVOICE_AMOUNT_REQUEST]: changeInvoiceAmountRequest,
  [Types.CHANGE_INVOICE_AMOUNT_SUCCESS]: changeInvoiceAmountSuccess,

  [Types.UPDATE_ADMIN_INVOICE_REQUEST]: updateAdminInvoiceRequest,
  [Types.UPDATE_ADMIN_INVOICE_SUCCESS]: updateAdminInvoiceSuccess,

  [Types.CONFIRM_INVOICE_REQUEST]: confirmInvoiceRequest,
  [Types.CONFIRM_INVOICE_SUCCESS]: confirmInvoiceSuccess,

  [Types.ADMIN_INVOICES_FAILURE]: adminInvoicesFailure,

  [Types.CLEAR_ADMIN_INVOICES_FILTERS]: clearAdminInvoicesFilters,

  [Types.ADMIN_INVOICES_SET_PROP]: adminInvoicesSetProp,

  [Types.GET_ADMIN_INVOICES_EXPORT_REQUEST]: getAdminInvoicesExportRequest,
  [Types.GET_ADMIN_INVOICES_EXPORT_SUCCESS]: getAdminInvoicesExportSuccess,
  [Types.GET_ADMIN_INVOICES_EXPORT_FAILURE]: getAdminInvoicesExportFailure,

  [Types.LOGOUT]: logout,

  [preferencesSuccess.type]: applyPreferences
})
